import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ref, get } from "firebase/database";
import { database } from "../firebaseConfig";
import "./ProductDetails.css";
import RelatedProducts from "../components/RelatedProducts";

// ContactForm Component
function ContactForm({ productName, token, onClose }) {
    const [result, setResult] = useState("");

    const onSubmit = async (event) => {
        event.preventDefault();
        setResult("Sending....");
        const formData = new FormData(event.target);
        formData.append("access_key", token);

        try {
            const response = await fetch("https://api.web3forms.com/submit", {
                method: "POST",
                body: formData,
            });
            const data = await response.json();
            if (data.success) {
                setResult("Form Submitted Successfully");
                event.target.reset();
            } else {
                console.error("Error", data);
                setResult(data.message);
            }
        } catch (error) {
            console.error("Submission error:", error);
            setResult("Submission failed");
        }
    };

    return (
        <div className="product-discription-modal-overlay">
            <div className="product-discription-modal">
                <button className="product-discription-modal-close" onClick={onClose}>
                    &times;
                </button>
                <h2>Contact Us</h2>
                <form onSubmit={onSubmit}>
                    <input type="text" name="name" placeholder="Your Name" required />
                    <input type="tel" name="phone" placeholder="Your Phone" required />
                    <input type="email" name="email" placeholder="Your Email" required />
                    <textarea name="message" placeholder="Your Query" required></textarea>
                    <input type="hidden" name="product" value={productName} />
                    <button type="submit">Submit Form</button>
                </form>
                <span>{result}</span>
            </div>
        </div>
    );
}

const ProductDetails = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const product = location.state?.product;

    const [cardsPerRow, setCardsPerRow] = useState(3);
    const [showContactForm, setShowContactForm] = useState(false);
    const [prodConfig, setProdConfig] = useState({
        buy_now: { visible: false },
        add_cart: { visible: false },
        enquire: { visible: false, token: "" },
    });

    const getInitialMainImage = (prod) =>
        prod && (prod.detail_images?.[0] || prod.image)
            ? prod.detail_images?.[0] || prod.image
            : "https://via.placeholder.com/600x600";

    const [mainImage, setMainImage] = useState(getInitialMainImage(product));

    useEffect(() => {
        setMainImage(getInitialMainImage(product));
    }, [product]);

    useEffect(() => {
        const updateCardsPerRow = () => {
            setCardsPerRow(window.innerWidth <= 768 ? 2 : 3);
        };
        updateCardsPerRow();
        window.addEventListener("resize", updateCardsPerRow);
        return () => window.removeEventListener("resize", updateCardsPerRow);
    }, []);

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const snapshot = await get(ref(database, "product_discription"));
                const fetchedConfig = snapshot.exists() ? snapshot.val() : {};
                setProdConfig((prev) => ({ ...prev, ...fetchedConfig }));
            } catch (error) {
                console.error("Error fetching product configuration:", error);
            }
        };
        fetchConfig();
    }, []);

    if (!product) {
        return (
            <div className="product-details-page">
                <p>Product details not available.</p>
                <button className="back-button" onClick={() => navigate(-1)}>
                    Go Back
                </button>
            </div>
        );
    }

    const pageTitle = product.metaTitle
        ? product.metaTitle
        : product.name
            ? `${product.name} - Product Details`
            : "Product Details";
    const metaDescription = product.metaDescription
        ? product.metaDescription
        : product.description || "Product details";
    const metaKeywords = product.metaKeywords ? product.metaKeywords : product.name || "product";

    const handleThumbnailClick = (imgUrl) => {
        setMainImage(imgUrl);
    };

    const handleRelatedProductClick = (relatedProduct) => {
        navigate("/product-details", { state: { product: relatedProduct } });
    };

    return (
        <div className="product-details-page" style={{ position: "relative", paddingTop: "60px" }}>
            {/* 🔙 Back Arrow Button */}
            <button
                onClick={() => navigate(-1)}
                style={{
                    // position: "absolute",
                    top: "20px",
                    left: "20px",
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    backgroundColor: "#f8d7da",
                    border: "none",
                    fontSize: "20px",
                    fontWeight: "bold",
                    cursor: "pointer",
                    color: "#333",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "background-color 0.3s ease",
                }}
                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#c2185b")}
                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "#f8d7da")}
                aria-label="Go back"
            >
                ←
            </button>

            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={metaDescription} />
                <meta name="keywords" content={metaKeywords} />
            </Helmet>

            <h1 className="product-details-title">{product.name}</h1>

            <div className="product-content">
                {/* Images */}
                <div className="product-images-section">
                    {product.detail_images && product.detail_images.length > 0 ? (
                        <div className="thumbnail-column">
                            {product.detail_images.map((imgUrl, index) => (
                                <img
                                    key={index}
                                    src={imgUrl}
                                    alt={`Thumbnail ${index}`}
                                    className={`thumbnail-image ${mainImage === imgUrl ? "active-thumbnail" : ""}`}
                                    onClick={() => handleThumbnailClick(imgUrl)}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="thumbnail-column">
                            <img
                                src={getInitialMainImage(product)}
                                alt="Fallback"
                                className="thumbnail-image active-thumbnail"
                            />
                        </div>
                    )}
                    <div className="main-image-container">
                        <img src={mainImage} alt="Main Product" className="main-image" />
                    </div>
                </div>

                {/* Product Info */}
                <div className="product-details-info">
                    {product.description && (
                        <p className="product-details-description">{product.description}</p>
                    )}
                    {product.sellingPrice && (
                        <p className="product-details-price">Price: ₹{product.sellingPrice}</p>
                    )}
                    {product.mrp && (
                        <p className="product-details-mrp">
                            MRP: <span className="line-through">₹{product.mrp}</span>
                        </p>
                    )}
                    {product.discount && (
                        <p className="product-details-discount">
                            <span className="discount-percentage">-{product.discount}%</span> (You Save ₹
                            {product.mrp && product.sellingPrice
                                ? product.mrp - product.sellingPrice
                                : ""}
                            )
                        </p>
                    )}
                    {product.rating && (
                        <p className="product-details-rating">Rating: {product.rating}</p>
                    )}
                    {product.additional_info && (
                        <p className="product-details-additional">{product.additional_info}</p>
                    )}

                    <div className="action-buttons">
                        {prodConfig.buy_now.visible && (
                            <button className="buy-now-button">Buy Now</button>
                        )}
                        {prodConfig.add_cart.visible && (
                            <button className="add-to-cart-button">Add to Cart</button>
                        )}
                        {prodConfig.enquire.visible && (
                            <button className="buy-now-button" onClick={() => setShowContactForm(true)}>
                                Inquire
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {product.related_products && (
                <RelatedProducts
                    relatedProductsStr={product.related_products}
                    cardsPerRow={cardsPerRow}
                    cardsPerRowPhoneView={2}
                    onProductClick={handleRelatedProductClick}
                />
            )}

            {showContactForm && (
                <ContactForm
                    productName={product.name}
                    token={prodConfig.enquire.token}
                    onClose={() => setShowContactForm(false)}
                />
            )}
        </div>
    );
};

export default ProductDetails;
